/* eslint-disable max-lines, max-depth, max-depth, max-statements, complexity */
/********************************/
/*          About vuex          */
/********************************/
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapState, mapActions } from "vuex";
import atUtil from "../../../../pages/individualActiveTest/individualActiveTestUtil";
import {
  FLOW,
  RES_TYPE,
  NRC_CODE,
  ERR_KEY,
  MNTR_STATUS,
  ERR_TYPE,
  DATA_TYPE,
  FLOW_ERROR,
  REQUEST_RESULT,
  AT_EXE_TYPE
} from "../../../../pages/individualActiveTest/individualActiveTestConstants.js";
import apiService from '../../../../../share/util/api/service';
import config from "../../../../../share/config";
import {
  REQUEST_STATUS_COMPLETED,
  REQUEST_STATUS_FAILED
} from '../../../../../share/util/api/polling';
import session from "../../../../../share/session";
import individualDiagnose from "../../../../../share/util/individualDiagnose";
import loading from "../../../../../share/components/vue/ProcessingIcon";
import alert from "../../../../../share/alert";

const AT_EXE = {
  STATUS: "status",
  MNTR_STATUS: "monitoring_status",
  USER_DATA: "user_data",
  RES_TYPE: "res_type",
  VAL: "value",
  REQUEST_RESULT: "request_result",
};

// 分岐処理があるSP機能要求ID
const SP_REQ = {
  REQ_ACT_31_NIN_NEXM_NSTP_NSTM_NRD_NLP:
    "REQ.ACT.31.NIN.NEXM.NSTP.NSTM.NRD.NLP",
  REQ_ACT_2F_NIN_EXM_STP_NSTM_NRD_LP: "REQ.ACT.2F.NIN.EXM.STP.NSTM.NRD.LP",
  SP2: "REQ.ACT.SP2",
  SP6: "REQ.ACT.SP6",
  SP16: "REQ.ACT.SP16",
  SP23: "REQ.ACT.SP23",
  SP25: "REQ.ACT.SP25",
  SP26: "REQ.ACT.SP26",
  SP28: "REQ.ACT.SP28",
  SP29: "REQ.ACT.SP29",
};

// エラー時に駆動後も停止処理なしでエラー終了する機能要求ID
const NO_ERROR_STOP_REQ = [
  "REQ.ACT.31.NIN.NEXM.NSTP.NSTM.NRD.NLP"
];

const PARAM = {
  ID: "active_test_id",
  ID_REQUIREMENT: "requirement_id",
  DID: "did",
  RID: "rid",
  CTRL_STATE: "control_state",
  RANGE: "operation_rules",
  MIN: "min",
  MAX: "max",
  STEP: "step",
  TYPE: "data_type",
  UNIT: "unit",
  USER_DATA: "user_data",
  READOUT: "readout_data",
  RECORD: "data_record",
  RES_TYPE: "res_type",
  VAL: "value",
  MNTR_STATUS: "monitoring_status",
  IS_ERR: "is_error",
  SID0X2E_SID0X2F: "sid0x2E_sid0x2F",
  EXE_MNTR_TERMINATE: "execution_monitoring_terminate_condition",
  EXE_MNTR_CONTINUE: "execution_monitoring_continuate_condition",
  STOP_MNTR_TERMINATE: "stop_monitoring_terminate_condition",
  STOP_MNTR_CONTINUE: "stop_monitoring_continuate_condition",
};

export default {
  name: "activeTestBlankPanel",
  data() {
    return {
      flow: FLOW.INIT,
      didList: [],
      // 変換情報
      conversionInfoList: [],
      // AT文言 
      message: "",
      // コントロールパネル情報
      panelInfo: {},
      // 停止中フラグ(doStart, doStop, エラーの停止処理以外で変更してはならない)
      isStopping: false,
      displayProcessing: false,
      // サインアウト
      signout: false,
      // 駆動/停止/エラー時のマーカ登録用オブジェクト
      markInfo: {
        type: null,
        info: []
      }
    };
  },
  props: {
    // 選択されたAT項目
    selectedAtFunc: { type: Object, required: true },
    // 選択項目名称i18nキー
    i18nMenuTitleKey: { type: String, default: "", required: false },
    // 駆動中フラグ、親画面の画面遷移判定で使用
    isDriving: { type: Boolean, required: true },
    // DM駆動中フラグ
    inExecution: { type: Boolean, required: true },
    // DM開始時刻
    baseTime: { type: Number, default: null, required: false },
  },

  components: {
    "processing-view": loading,
  },

  watch: {
    // 言語設定変更時処理
    language: {
      handler() {
        this.clearAtMsg();
      },
    },
  },

  computed: {
    ...mapState({
      language: (state) => state.systems.language,
      netAppId: (state) => state.netAppConnector.pairingAppId,
      vehicleInfo: (state) => state.vehicle.vehicleInfo,
      individualMenus: (state) => state.vehicle.individualMenus,
      isTestMode: (state) => state.netAppConnector.isTestMode,
    }),

    projectId() {
      return this.vehicleInfo ? this.vehicleInfo.vehicle.project.id : null;
    },

    activeTestId() {
      return this.selectedAtFunc[PARAM.ID];
    },

    requirementId() {
      return this.selectedAtFunc[PARAM.ID_REQUIREMENT];
    },

    systemId() {
      return individualDiagnose.getCurrentSystemId(this);
    },

    rid() {
      return this.selectedAtFunc[PARAM.RID];
    },

    controlState() {
      return this.selectedAtFunc[PARAM.CTRL_STATE];
    },

    sid0x2E_sid0x2F() {
      return this.selectedAtFunc[PARAM.SID0X2E_SID0X2F];
    },

    isDriveFlow() {
      return [
        FLOW.PRE_EXECUTE,
        FLOW.EXECUTE,
        FLOW.CHECK_EXE,
        FLOW.WAIT_STOP,
      ].includes(this.flow);
    },

    driveBtnLabel() {
      return !this.isDriveFlow
        ? this.$t("individual.button_active_test_drive")
        : this.$t("individual.button_data_monitor_stop");
    },

    disabled() {
      const excludedIds = [
        "REQ.ACT.31.NIN.EXM.NSTP.NSTM.NRD.NLP",
        "REQ.ACT.SP2",
        "REQ.ACT-13",
        "REQ.ACT-30"
      ];

      const drive_flow = !excludedIds.includes(this.requirementId) ?
        [
          FLOW.STANDBY,
          FLOW.EXECUTE,
          FLOW.CHECK_EXE,
          FLOW.WAIT_STOP,
        ] :
        // REQ.ACT.31.NIN.EXM.NSTP.NSTM.NRD.NLPの場合、実行中は停止ボタンの押下不可
        [
          FLOW.STANDBY,
          FLOW.WAIT_STOP,
        ];

      const disabled = {
        drive: !drive_flow.includes(this.flow),
      };
      return disabled;
    },
    // HEV系ATのDM表示に用いる
    isHev() { return this.atInfo && (this.atInfo.length > 0); },
    atInfo() { return this.selectedAtFunc?.at_info; },
  },

  methods: {
    ...mapActions("auth", ["logout", "resetSession"]),

    /**
     * AT項目選択ボタンクリック時の処理
     * AT項目選択 → DM項目選択 遷移時に行う処理を記述する
     */
    btnAtSelectOnClicked() {
      // データ読み出し実行フラグをfalseに切り替え
      this.$emit("toggle-read-executed", false);
      // 初期情報取得
      this.getInitInfo();
      // 初期表示処理
      this.displayInit();
    },

    driveBtn() {
      if (!this.isDriving) {
        // 駆動
        this.startDrive();
      } else {
        // 停止
        this.stopDrive();
      }
    },

    /** エラー時のAT終了処理 */
    errTerminate() {
      this.$emit("err-terminate");
    },

    /** 駆動ボタン押下処理 */
    async startDrive() {
      // SP2対応: 駆動が完了するまで、データモニタの停止を押下できなくする対応
      if (this.requirementId === "REQ.ACT.SP2") {
        this.displayProcessing = true;
      }
      this.$emit("toggle-drive", true);
      this.flow = FLOW.PRE_EXECUTE;
      this.clearAtMsg();

      // 開始マーカの登録実行
      this.markInfo.type = AT_EXE_TYPE.START; // 駆動をセット
      await this.$emit("save-active-test-executeMark", this.markInfo);

      const response = await atUtil.doStandby(this, true);
      if (response) {
        switch (response.data.status) {
          case REQUEST_STATUS_COMPLETED:
            {
              let checkResponse = true;
              if (response.data.user_data && Array.isArray(response.data.user_data)) {
                for (const userData of response.data.user_data) {
                  if (userData.res_type !== RES_TYPE.OK) {
                    checkResponse = false;
                    break;
                  }
                }
              }
              // 正常終了
              if (checkResponse) {
                if (this.requirementId === 'REQ.ACT-13') {
                  // 実行前確認処理(ルーチン結果確認)
                  if (!await this.doConfirm(0)) {
                    return;
                  }
                }
                // AT実行
                await this.doStart();
              }
              else {
                // 共通エラーへ遷移
                this.flow = FLOW.ERR;
                this.doErrorProcess(ERR_KEY.COMMON);
              }
            }
            break;
          default:
            // 共通エラーへ遷移
            this.flow = FLOW.ERR;
            this.doErrorProcess(ERR_KEY.COMMON);
            break;
        }
      }
    },

    /** 停止ボタン押下処理 */
    async stopDrive() {
      // 停止指示待ち状態以外の場合に状態を停止に更新する
      if (this.flow !== FLOW.WAIT_STOP) {
        this.flow = FLOW.STOP;
      } else if (
        this.flow === FLOW.WAIT_STOP &&
        this.requirementId === "REQ.ACT-27"
      ) {
        this.isStopping = true;
        this.flow = FLOW.STOP;
      }

      // 停止マーカの登録実行
      this.markInfo.type = AT_EXE_TYPE.END; // 停止をセット
      await this.$emit("save-active-test-executeMark", this.markInfo);

      const response = await this.doStop();

      // HEV系AT対応: 停止処理完了時、IO/Routine Control項目の値を空にする
      switch (this.requirementId) {
        case "REQ.ACT-12":
        case "REQ.ACT-27":
          this.$emit("dm-change-to-blank");
          break;
        default:
          break;
      }

      if (response) {
        if (
          response.data.status === REQUEST_STATUS_COMPLETED &&
          (
            (
              this.requirementId !== "REQ.ACT-12" &&
              this.requirementId !== "REQ.ACT-27"
            ) || this.isStopping
          ) // 再実行された場合は以降の処理を実行しない
        ) {
          const result = await this.doTerminate();
          if (result) {
            // 初期表示処理
            this.displayInit();

            // アクティブ文言のクリア判定
            if (atUtil.isClearMessageOnStop(this.requirementId)) {
              this.clearAtMsg();
            }
          } else {
            this.flow = FLOW.ERR;
            this.doErrorProcess(ERR_KEY.COMMON);
          }
          return;
        }
      } else if (this.requirementId !== "REQ.ACT.31.NIN.NEXM.NSTP.NSTM.NRD.NLP") {
        // 共通エラーへ遷移
        this.flow = FLOW.ERR;
        this.doErrorProcess(ERR_KEY.COMMON);
      }
    },

    /** 変換情報、初期値を取得 */
    async getInitInfo() {
      switch (this.requirementId) {
        case SP_REQ.SP29: {
          // 変換情報を取得
          const convRes = await this.getATConversion([this.activeTestId]);
          if (!convRes) {
            // 処理を中断
            return;
          }

          // ブランクでは変換情報は1つ
          this.conversionInfoList = convRes.data.conversion_list;

          // didList
          const didList = this.conversionInfoList.map((conv) => {
            return conv[PARAM.DID];
          });
          this.didList = didList;
          break;
        }
        default:
          break;
      }
    },

    /** 初期表示処理 */
    displayInit() {
      // 待機に遷移
      this.flow = FLOW.STANDBY;
      this.$emit("toggle-drive", false);
    },

    /* パラメータat_statusに格納するオブジェクトを返す */
    getAtStatus() {
      // HEV系ATのDM１行目にControl結果表示かつDM実行中のみ設定
      return this.isHev && this.inExecution && this.baseTime ? {
        project_id: parseInt(this.projectId),
        registered_at: this.baseTime,
        elapsed_time: (Date.now() - this.baseTime) * 1000
      } : null;
    },

    /** AT文言の削除 */
    clearAtMsg() {
      this.message = "";
    },

    /******************** API ********************/

    /**
     * 変換情報取得処理
     * @param {array} at_id_list 選択項目の不変idList
     * @returns {array} conversionInfo 変換情報
     */
    async getATConversion(at_id_list) {
      const response = await individualDiagnose.syncApi(
        this,
        // GET API 呼び出しのコールバック
        async () => {
          return await apiService.getParamCall(
            apiService.DMAT,
            "/netApps/" +
            this.netAppId +
            "/systems/" +
            this.systemId +
            "/activeTestConversion",
            {
              active_test_id_list: JSON.stringify(at_id_list),
            }
          );
        },
        // エラー発生のコールバック
        (errorType) => {
          errorType;
          atUtil.showUnexpectedError(this);
        },
        // GET API レスポンスチェックのコールバック
        (getApiResponse) => {
          // monitor_items が入っていれば成功とする
          return getApiResponse &&
            getApiResponse.data &&
            getApiResponse.data.conversion_list
            ? ""
            : config.INDIVIDUAL_API_CALL_ERROR_GET_INVALID_RESPONSE;
        },
        // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
        (result) => {
          atUtil.validateErrorResult(this, result);
        }
      );

      return response;
    },

    /** 
     * ルーチン結果確認処理 
     * @param {Number} ridIndex ridのインデックス
     * @returns {Boolean} 成否
     */
    async doConfirm(ridIndex) {
      // ルーチン結果確認
      return await atUtil.doConfirm(
        this,
        this.rid[ridIndex],
        // レスポンスチェックがcompletedかつres_type:"ok"時のコールバック(booleanを返す)
        async (response, self) => {
          const monitoringStatusData = atUtil.getMonitoringStatusData(response.data[PARAM.USER_DATA]);
          const status = atUtil.getMonitoringStatus(monitoringStatusData[PARAM.VAL], self.requirementId);
          switch (self.requirementId) {
            case 'REQ.ACT-13':
              // 確認結果により処理を分岐
              switch (status) {
                case "01": // routine実施中 -> 処理完了として正常終了
                  {
                    // AT文言を表示
                    self.message = atUtil.getAtMsg(
                      self,
                      self.systemId,
                      self.requirementId,
                      parseInt(status, 16),
                    );

                    // 「外部均等充電を実行中です」ダイアログを表示
                    alert.showSuccess(
                      self,
                      null,
                      self.$t(`at${self.systemId}.${self.activeTestId.slice(0, -2)}.charging_started`),
                      async () => {
                        // Terminate中に他の操作をさせないようにする
                        self.displayProcessing = true;
                        await atUtil.doTerminate(self, false);
                        self.displayInit();
                        self.$emit('at-failure');
                        self.displayProcessing = false;
                      }
                    );

                    return false;
                  }
                case "00": // routine未実施 -> 処理続行
                case "02": // routine正常終了 -> 処理続行
                  // AT文言を表示
                  self.message = atUtil.getAtMsg(
                    self,
                    self.systemId,
                    self.requirementId,
                    parseInt(status, 16),
                  );
                  return true;
                default: // 上記以外(03-FF) -> STATUSエラーを表示して異常終了
                  {
                    // STATUSエラーへ遷移
                    const atMsg = atUtil.getStatusMsg(
                      self,
                      self.systemId,
                      parseInt(status, 16),
                    );
                    self.doErrorProcess(ERR_KEY.STATUS, atMsg);
                    return false;
                  }
              }
            default:
              return false;
          }
        },
        // レスポンスチェックがfailed時のコールバック(booleanを返す)
        async (self) => {
          switch (self.requirementId) {
            case 'REQ.ACT-13':
              // 共通エラーへ遷移
              await self.doErrorProcess(ERR_KEY.COMMON);
              return false;
            default:
              return false;
          }
        }
      );
    },

    /** 駆動開始 */
    async doStart() {
      this.isStopping = false;

      // API実行パラメータ作成
      const token = session.getToken();
      const csVal = this.controlState && this.controlState.length >= 1
        ? this.controlState[0][PARAM.VAL]
        : null;
      const atStatus = this.getAtStatus();
      const requestBody = atUtil.genReqBodyStart(
        token.id_token,
        this.selectedAtFunc,
        csVal,
        undefined,
        atStatus
      );

      // start API実行
      const response = await atUtil.pollingAtApi(
        this,
        this.netAppId,
        this.systemId,
        this.activeTestId,
        requestBody,
        // エラー発生のコールバック
        (errorType) => {
          errorType;
          atUtil.showUnexpectedError(this);
        },
        // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
        (result) => {
          atUtil.validateErrorResult(this, result);
        },
        // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
        (result) => {
          atUtil.validateErrorResult(this, result);
        },
        // GET API レスポンスチェックがin_progress時のコールバック
        () => {
          this.flow = FLOW.EXECUTE;
          return config.INDIVIDUAL_POLLING_RESULT_CONTINUE;
        },
        // GET API レスポンスチェックがcompleted時のコールバック
        (getApiResponse, self) => {
          if (atUtil.isErrorAbort(getApiResponse.data[AT_EXE.REQUEST_RESULT])) {
            // 開始のポーリングでCCエラー中断のレスポンスは、データモニタでエラーとなった場合のため、通信エラーへ遷移
            // ただしデータモニタ起点で既にエラー処理をおこなっている場合は、そのままポーリング終了
            if (!FLOW_ERROR.includes(self.flow)) {
              self.flow = FLOW.ERR;
              // エラー時に停止処理なしでエラー終了するものは、ERR_KEY.COMMONでterminateのみ実施
              const errorKey = NO_ERROR_STOP_REQ.includes(this.requirementId) ? ERR_KEY.COMMON : ERR_KEY.COMMUNICATION;
              self.doErrorProcess(errorKey);
            }
            return config.INDIVIDUAL_POLLING_RESULT_FINISH;
          }

          let result = config.INDIVIDUAL_POLLING_RESULT_ERROR;
          const monitoringStatusData = atUtil.getMonitoringStatusData(getApiResponse.data[PARAM.USER_DATA]);
          const isExeOnly =
            getApiResponse.data[PARAM.USER_DATA].length === 0 &&
            getApiResponse.data[PARAM.MNTR_STATUS] === MNTR_STATUS.COMPLETE;
          if (
            isExeOnly ||
            !(PARAM.MNTR_STATUS in getApiResponse.data) ||
            !getApiResponse.data[PARAM.MNTR_STATUS]
          ) {
            // 実行
            if (isExeOnly || monitoringStatusData[PARAM.RES_TYPE] === RES_TYPE.OK) {
              // 正常終了
              switch (self.requirementId) {
                case "REQ.ACT.2F.NIN.EXM.STP.STM.RD.NLP":
                case "REQ.ACT.2F.NIN.EXM.STP.NSTM.NRD.LP":
                case "REQ.ACT.31.NIN.EXM.STP.STM.NRD.NLP":
                case "REQ.ACT.31.NIN.EXM.STP.NSTM.NRD.NLP":
                case "REQ.ACT.31.NIN.EXM.NSTP.NSTM.NRD.NLP":
                case "REQ.ACT.SP2":
                case "REQ.ACT.SP16":
                case "REQ.ACT.SP18":
                case "REQ.ACT.SP19":
                case "REQ.ACT.SP21":
                case "REQ.ACT.SP23":
                case "REQ.ACT.SP25":
                case "REQ.ACT.SP26":
                case "REQ.ACT.SP28":
                case "REQ.ACT-12":
                case "REQ.ACT-13":
                case "REQ.ACT-27":
                  // 実行監視に遷移
                  if (!self.isStopping) {
                    self.flow = FLOW.CHECK_EXE;
                  }
                  result = config.INDIVIDUAL_POLLING_RESULT_CONTINUE;
                  break;
                case "REQ.ACT.31.NIN.NEXM.NSTP.NSTM.NRD.NLP":
                case "REQ.ACT.SP6":
                  self.message = atUtil.getAtMsg(
                    self,
                    self.systemId,
                    self.requirementId,
                    null,
                    self.isDriveFlow
                  );
                  if (!self.isStopping) {
                    self.flow = FLOW.EXECUTE;
                  }
                  result = config.INDIVIDUAL_POLLING_RESULT_FINISH;
                  break;
                case "REQ.ACT.SP29": {
                  // DID変換値を出力・表示する
                  let conversionInfo = null;
                  for (const convInfo of this.conversionInfoList) {
                    if (convInfo[PARAM.DID] === this.sid0x2E_sid0x2F[0]) {
                      conversionInfo = convInfo;
                    }
                  }

                  // valueをDID変換し、HexとFlgの場合は翻訳して表示する
                  const hexKey = atUtil.convCtrlStateValue(
                    this.controlState[0].value,
                    conversionInfo
                  );

                  let message = hexKey;
                  if (conversionInfo.info[PARAM.TYPE] !== DATA_TYPE.VAL) {
                    const did = conversionInfo[PARAM.DID];

                    // diag/配下の検索キー
                    const findFFDi18nKey = individualDiagnose.getFFDi18nKey(this, this.individualMenus ? this.individualMenus.menu_list : []);
                    const keyDiag = `${findFFDi18nKey}.${did}_${hexKey}`;
                    // at/did/配下の検索キー
                    const keyAt = `atDid${self.systemId}.${did}_${hexKey}`;
                    if (this.$te(keyDiag)) {
                      message = this.$t(keyDiag);
                    } else if (this.$te(keyAt)) {
                      message = this.$t(keyAt);
                    }
                  }
                  self.message = message;

                  if (!self.isStopping) {
                    self.flow = FLOW.EXECUTE;
                  }
                  result = config.INDIVIDUAL_POLLING_RESULT_FINISH;
                  break;
                }
                case "REQ.ACT-30":
                  // ダイアログ表示
                  alert.showSuccess(
                    self,
                    null,
                    self.$t(`at${this.systemId}.${this.activeTestId.slice(0, -2)}.start_charging`),
                    async () => {
                      // Terminate中に他の操作をさせないようにする
                      self.displayProcessing = true;
                      await atUtil.doTerminate(self, false);
                      self.displayInit();
                      self.$emit('at-failure');
                      self.displayProcessing = false;
                    }
                  );
                  result = config.INDIVIDUAL_POLLING_RESULT_FINISH;
                  break;
                default:
                  break;
              }
            } else {
              // 異常終了
              switch (self.requirementId) {
                case "REQ.ACT.2F.NIN.EXM.STP.STM.RD.NLP":
                case "REQ.ACT.2F.NIN.EXM.STP.NSTM.NRD.LP":
                case "REQ.ACT.31.NIN.EXM.STP.STM.NRD.NLP":
                case "REQ.ACT.31.NIN.EXM.STP.NSTM.NRD.NLP":
                case "REQ.ACT.SP2":
                case "REQ.ACT.SP6":
                case "REQ.ACT.SP19":
                case "REQ.ACT.SP26":
                  // 通信エラーへ遷移
                  self.flow = FLOW.ERR_COMMUNICATE;
                  self.doErrorProcess(ERR_KEY.COMMUNICATION);
                  result = config.INDIVIDUAL_POLLING_RESULT_FINISH;
                  break;
                case "REQ.ACT.31.NIN.NEXM.NSTP.NSTM.NRD.NLP":
                case "REQ.ACT.31.NIN.EXM.NSTP.NSTM.NRD.NLP":
                case "REQ.ACT.SP16":
                case "REQ.ACT.SP18":
                  // 共通エラーへ遷移
                  self.flow = FLOW.ERR;
                  self.doErrorProcess(ERR_KEY.COMMON);
                  result = config.INDIVIDUAL_POLLING_RESULT_FINISH;
                  break;
                case "REQ.ACT.SP21":
                case "REQ.ACT.SP23":
                case "REQ.ACT.SP29":
                case "REQ.ACT-12":
                case "REQ.ACT-27": {
                  const nrc = atUtil.getNRCStatus(monitoringStatusData[AT_EXE.VAL]);
                  // NRC0x22の場合はNRCエラー、その他は通信エラー
                  const errKey = [NRC_CODE.X22].includes(nrc)
                    ? ERR_KEY.NRC : ERR_KEY.COMMUNICATION;
                  self.doErrorProcess(errKey);
                  result = config.INDIVIDUAL_POLLING_RESULT_FINISH;
                  break;
                }
                case "REQ.ACT-30": {
                  const nrc = atUtil.getNRCStatus(monitoringStatusData[AT_EXE.VAL]);
                  // NRC0x22の場合はNRCエラー、その他は共通エラーを表示
                  const errKey = [NRC_CODE.X22].includes(nrc)
                    ? ERR_KEY.NRC : ERR_KEY.COMMON;
                  atUtil.showUnexpectedError(
                    self,
                    errKey,
                    self.requirementId,
                    null,
                    async () => {
                      // Terminate中に他の操作をさせないようにする
                      self.displayProcessing = true;
                      await atUtil.doTerminate(self, true);
                      self.displayInit();
                      self.$emit('at-failure');
                      self.displayProcessing = false;
                    }
                  );
                  result = config.INDIVIDUAL_POLLING_RESULT_FINISH;
                  break;
                }
                case "REQ.ACT.SP25":
                case "REQ.ACT.SP28": {
                  const nrc = atUtil.getNRCStatus(monitoringStatusData[AT_EXE.VAL]);
                  // NRC0x11, NRC0x12, NRC0x31のいずれかの場合はNRCエラー、その他は通信エラー
                  const errKey = [NRC_CODE.X11, NRC_CODE.X12, NRC_CODE.X31].includes(nrc)
                    ? ERR_KEY.NRC : ERR_KEY.COMMUNICATION;
                  self.doErrorProcess(errKey);
                  result = config.INDIVIDUAL_POLLING_RESULT_FINISH;
                  break;
                }
                case "REQ.ACT-13":
                  if (monitoringStatusData[PARAM.VAL] === "" && monitoringStatusData[PARAM.RES_TYPE] === RES_TYPE.NG) {
                    // タイムアウトエラー
                    self.doErrorProcess(ERR_KEY.TIMEOUT);
                  } else {
                    const nrc = atUtil.getNRCStatus(monitoringStatusData[AT_EXE.VAL]);
                    // NRC0x22の場合はNRCエラー、その他は共通エラーを表示
                    const errKey = [NRC_CODE.X22].includes(nrc)
                      ? ERR_KEY.NRC : ERR_KEY.COMMON;
                    atUtil.showUnexpectedError(
                      self,
                      errKey,
                      self.requirementId,
                      null,
                      async () => {
                        // Terminate中に他の操作をさせないようにする
                        self.displayProcessing = true;
                        await atUtil.doTerminate(self, true);
                        self.displayInit();
                        self.$emit('at-failure');
                        self.displayProcessing = false;
                      }
                    );
                  }
                  result = config.INDIVIDUAL_POLLING_RESULT_FINISH;
                  break;
                default:
                  break;
              }
            }
          } else {
            // 実行監視
            if (
              monitoringStatusData[PARAM.RES_TYPE] === RES_TYPE.OK
            ) {
              if (!self.isStopping) {
                self.flow = FLOW.CHECK_EXE;
              }
              // 正常終了
              const status = atUtil.getMonitoringStatus(monitoringStatusData[PARAM.VAL], self.requirementId);
              switch (self.requirementId) {
                case "REQ.ACT.2F.NIN.EXM.STP.STM.RD.NLP":
                case "REQ.ACT.31.NIN.EXM.STP.STM.NRD.NLP":
                case "REQ.ACT.31.NIN.EXM.STP.NSTM.NRD.NLP":
                case "REQ.ACT.SP18":
                case "REQ.ACT.SP19":
                case "REQ.ACT-13":
                  if (
                    self.selectedAtFunc[PARAM.EXE_MNTR_CONTINUE] &&
                    self.selectedAtFunc[PARAM.EXE_MNTR_CONTINUE].includes(
                      status
                    )
                  ) {
                    // 実行監視継続条件
                    self.message = atUtil.getAtMsg(
                      self,
                      self.systemId,
                      self.requirementId,
                      parseInt(status, 16),
                      self.isDriveFlow,
                      () => { },
                      true
                    );
                    result = config.INDIVIDUAL_POLLING_RESULT_CONTINUE;
                  } else if (
                    self.selectedAtFunc[PARAM.EXE_MNTR_TERMINATE] &&
                    self.selectedAtFunc[PARAM.EXE_MNTR_TERMINATE].includes(status)
                  ) {
                    // 実行監視終了条件
                    if (self.requirementId === "REQ.ACT-13") {
                      // 「外部均等充電を開始しました」ダイアログを表示
                      alert.showSuccess(
                        self,
                        null,
                        self.$t(`at${self.systemId}.${self.activeTestId.slice(0, -2)}.start_charging`),
                        async () => {
                          // Terminate中に他の操作をさせないようにする
                          self.displayProcessing = true;
                          await atUtil.doTerminate(self, false);
                          self.displayInit();
                          self.$emit('at-failure');
                          self.displayProcessing = false;
                        }
                      );
                    }
                    // 停止へ遷移(コールバックではやらない)
                    result = config.INDIVIDUAL_POLLING_RESULT_FINISH;
                  } else if (self.requirementId === "REQ.ACT.SP18" && parseInt(status, 16) === 261) {
                    // REQ.ACT.SP18、且つ0x0105(261)の場合
                    // 停止指示待ち状態へ遷移(停止はせずに停止可能な状態にする)し、「アクティブテスト文言」に「停止」を表示する
                    self.flow = FLOW.WAIT_STOP;
                    self.message = atUtil.getAtMsg(
                      self,
                      self.systemId,
                      self.requirementId,
                      parseInt(status, 16),
                      false,
                      () => { },
                      true
                    );
                    result = config.INDIVIDUAL_POLLING_RESULT_CONTINUE;
                  } else {
                    // それ以外
                    // STATUSエラーへ遷移
                    const atMsg = atUtil.getStatusMsg(
                      self,
                      self.systemId,
                      parseInt(status, 16),
                    );
                    self.flow = FLOW.ERR_STATUS;
                    self.doErrorProcess(ERR_KEY.STATUS, atMsg);
                    result = config.INDIVIDUAL_POLLING_RESULT_FINISH;
                  }
                  break;
                case "REQ.ACT.31.NIN.NEXM.NSTP.NSTM.NRD.NLP":
                  self.message = atUtil.getAtMsg(
                    self,
                    self.systemId,
                    self.requirementId,
                    null,
                    self.isDriveFlow
                  );
                  result = config.INDIVIDUAL_POLLING_RESULT_FINISH;
                  break;
                case "REQ.ACT.2F.NIN.EXM.STP.NSTM.NRD.LP":
                case "REQ.ACT.SP16":
                  self.message = atUtil.getAtMsg(
                    self,
                    self.systemId,
                    self.requirementId,
                    null,
                    self.isDriveFlow
                  );
                  result = config.INDIVIDUAL_POLLING_RESULT_CONTINUE;
                  break;
                case "REQ.ACT-12":
                  // 停止押下後は、メッセージ取得しない
                  if (!self.isStopping) {
                    self.message = atUtil.getAtMsg(
                      self,
                      self.systemId,
                      self.requirementId,
                      null,
                      self.isDriveFlow
                    );
                  }
                  result = config.INDIVIDUAL_POLLING_RESULT_CONTINUE;
                  break;
                case "REQ.ACT.SP23":
                case "REQ.ACT.SP26":
                  if (
                    self.selectedAtFunc[PARAM.EXE_MNTR_CONTINUE] &&
                    self.selectedAtFunc[PARAM.EXE_MNTR_CONTINUE].includes(
                      status
                    )
                  ) {
                    // 実行監視継続条件
                    self.message = atUtil.getAtMsg(
                      self,
                      self.systemId,
                      self.requirementId,
                      parseInt(status, 16),
                      self.isDriveFlow
                    );
                    result = config.INDIVIDUAL_POLLING_RESULT_CONTINUE;
                  } else if (
                    self.selectedAtFunc[PARAM.EXE_MNTR_TERMINATE] &&
                    self.selectedAtFunc[PARAM.EXE_MNTR_TERMINATE].includes(status)
                  ) {
                    // 実行監視終了条件
                    // 後処理へ遷移(コールバックではやらない)
                    self.message = atUtil.getAtMsg(
                      self,
                      self.systemId,
                      self.requirementId,
                      parseInt(status, 16),
                      self.isDriveFlow
                    );
                    result = config.INDIVIDUAL_POLLING_RESULT_FINISH;
                  } else {
                    // それ以外
                    // 通信エラーへ遷移
                    self.flow = FLOW.ERR_COMMUNICATE;
                    self.doErrorProcess(ERR_KEY.COMMUNICATION);
                    result = config.INDIVIDUAL_POLLING_RESULT_FINISH;
                  }
                  break;
                case "REQ.ACT.31.NIN.EXM.NSTP.NSTM.NRD.NLP":
                case "REQ.ACT.SP2":
                case "REQ.ACT.SP21":
                case "REQ.ACT.SP25":
                case "REQ.ACT.SP28":
                  if (
                    self.selectedAtFunc[PARAM.EXE_MNTR_CONTINUE] &&
                    self.selectedAtFunc[PARAM.EXE_MNTR_CONTINUE].includes(
                      status
                    )
                  ) {
                    // 実行監視継続条件
                    self.message = atUtil.getAtMsg(
                      self,
                      self.systemId,
                      self.requirementId,
                      parseInt(status, 16),
                      self.isDriveFlow
                    );
                    result = config.INDIVIDUAL_POLLING_RESULT_CONTINUE;
                    // SP2対応: 停止フラグの上書きがされない箇所まで到達後、スピナーを消す
                    if (self.requirementId === "REQ.ACT.SP2") {
                      self.displayProcessing = false;
                    }
                  } else {
                    // それ以外
                    // STATUSエラーへ遷移
                    const atMsg = atUtil.getStatusMsg(
                      self,
                      self.systemId,
                      parseInt(status, 16),
                    );
                    self.flow = FLOW.ERR_STATUS;
                    self.doErrorProcess(ERR_KEY.STATUS, atMsg);
                    result = config.INDIVIDUAL_POLLING_RESULT_FINISH;
                  }
                  break;
                case "REQ.ACT-27":
                  // AT文言に駆動を表示して、停止待機 ※ルーチン実行は1回のみで、ユーザーの停止指示待ちの状態
                  self.message = atUtil.getAtMsg(
                    self,
                    self.systemId,
                    self.requirementId,
                    null,
                    true
                  );
                  result = config.INDIVIDUAL_POLLING_RESULT_FINISH;
                  break;
                default:
                  break;
              }
            } else {
              // 異常終了
              switch (self.requirementId) {
                case "REQ.ACT.31.NIN.EXM.NSTP.NSTM.NRD.NLP":
                case "REQ.ACT.31.NIN.NEXM.NSTP.NSTM.NRD.NLP":
                case "REQ.ACT.SP16":
                case "REQ.ACT.SP18":
                  // 共通エラーへ遷移
                  self.flow = FLOW.ERR;
                  self.doErrorProcess(ERR_KEY.COMMON);
                  result = config.INDIVIDUAL_POLLING_RESULT_FINISH;
                  break;
                case "REQ.ACT-12": {
                  const nrc = atUtil.getNRCStatus(monitoringStatusData[AT_EXE.VAL]);
                  // NRC0x22の場合はNRCエラー、その他は通信エラー
                  const errKey = [NRC_CODE.X22].includes(nrc)
                    ? ERR_KEY.NRC : ERR_KEY.COMMUNICATION;
                  self.doErrorProcess(errKey);
                  result = config.INDIVIDUAL_POLLING_RESULT_FINISH;
                  break;
                }
                case "REQ.ACT-13":
                  if (monitoringStatusData[PARAM.VAL] === "" && monitoringStatusData[PARAM.RES_TYPE] === RES_TYPE.NG) {
                    // タイムアウトエラー
                    self.doErrorProcess(ERR_KEY.TIMEOUT);
                  } else {
                    // NRC0x22の場合はNRCエラー、その他は通信エラー
                    const nrc = atUtil.getNRCStatus(monitoringStatusData[AT_EXE.VAL]);
                    const errKey = [NRC_CODE.X22].includes(nrc) ? ERR_KEY.NRC : ERR_KEY.COMMUNICATION;
                    atUtil.showUnexpectedError(
                      self,
                      errKey,
                      self.requirementId,
                      null,
                      async () => {
                        // Terminate中に他の操作をさせないようにする
                        self.displayProcessing = true;
                        await atUtil.doTerminate(self, true);
                        self.displayInit();
                        self.$emit('at-failure');
                        self.displayProcessing = false;
                      }
                    );
                  }
                  result = config.INDIVIDUAL_POLLING_RESULT_FINISH;
                  break;
                case "REQ.ACT-27":
                  if ([NRC_CODE.X21, NRC_CODE.X24].includes(atUtil.getNRCStatus(monitoringStatusData[AT_EXE.VAL]))) {
                    // 否定応答かつNRC0x21/0x24:ポーリング継続
                    // 停止ボタン押下可能に変更。但し、停止中の場合は実行状態を維持する(停止監視中に停止ボタンが押下できてしまうため)
                    if (!self.isStopping) { self.flow = FLOW.WAIT_STOP; }
                    // AT文言：停止中は「停止中」を維持し、停止中以外は「駆動」
                    self.message = self.isStopping
                      ? self.$t("individual.label_active_test_message_hev_stopping")
                      : atUtil.getAtMsg(
                        self,
                        self.systemId,
                        self.requirementId,
                        null,
                        true
                      );
                    result = config.INDIVIDUAL_POLLING_RESULT_CONTINUE;
                  } else {
                    // 上記以外：「停止」表示、共通エラー
                    self.message = atUtil.getAtMsg(
                      self,
                      self.systemId,
                      self.requirementId,
                      null,
                      false
                    );
                    self.doErrorProcess(ERR_KEY.COMMON);
                    result = config.INDIVIDUAL_POLLING_RESULT_FINISH;
                  }
                  break;
                case "REQ.ACT.2F.NIN.EXM.STP.STM.RD.NLP":
                case "REQ.ACT.2F.NIN.EXM.STP.NSTM.NRD.LP":
                case "REQ.ACT.31.NIN.EXM.STP.STM.NRD.NLP":
                case "REQ.ACT.31.NIN.EXM.STP.NSTM.NRD.NLP":
                case "REQ.ACT.SP2":
                case "REQ.ACT.SP19":
                case "REQ.ACT.SP21":
                case "REQ.ACT.SP23":
                case "REQ.ACT.SP25":
                case "REQ.ACT.SP26":
                case "REQ.ACT.SP28":
                default:
                  // 通信エラーへ遷移
                  self.flow = FLOW.ERR_COMMUNICATE;
                  self.doErrorProcess(ERR_KEY.COMMUNICATION);
                  result = config.INDIVIDUAL_POLLING_RESULT_FINISH;
                  break;
              }
            }
          }
          if (self.isStopping) {
            // 停止処理が開始されたのでstartのポーリングを終了させる
            result = config.INDIVIDUAL_POLLING_RESULT_FINISH;
          }
          return result;
        },
        // GET API レスポンスチェックがfailed時のコールバック
        (getApiResponse, self) => {
          self.flow = FLOW.ERR;
          // 共通エラーを表示
          self.doErrorProcess(ERR_KEY.COMMON);
        },
      );

      if (response) {
        const monitoringStatusData = atUtil.getMonitoringStatusData(response.data[PARAM.USER_DATA]);
        switch (response.data.status) {
          case REQUEST_STATUS_COMPLETED:
            // completed
            if (
              response.data[PARAM.USER_DATA].length > 0 &&
              monitoringStatusData[PARAM.RES_TYPE] === RES_TYPE.OK
            ) {
              // 正常終了
              switch (this.requirementId) {
                case "REQ.ACT.2F.NIN.EXM.STP.STM.RD.NLP":
                case "REQ.ACT.31.NIN.EXM.STP.STM.NRD.NLP":
                case "REQ.ACT.31.NIN.EXM.STP.NSTM.NRD.NLP":
                case "REQ.ACT.SP18":
                case "REQ.ACT.SP19":
                case "REQ.ACT.SP21":
                  if (!this.isStopping) {
                    // 停止へ遷移(ユーザ操作で停止していない場合のみ)
                    this.flow = FLOW.STOP;
                    await this.stopDrive();
                  }
                  break;
                case "REQ.ACT.SP23":
                case "REQ.ACT.SP26":
                case "REQ.ACT.31.NIN.EXM.NSTP.NSTM.NRD.NLP":
                  if (!this.isStopping) {
                    const result = await this.doTerminate();
                    if (result) {
                      // 初期表示処理(ユーザ操作で停止していない場合のみ)
                      this.displayInit();
                    } else {
                      this.flow = FLOW.ERR;
                      this.doErrorProcess(ERR_KEY.COMMON);
                    }
                  }
                  break;
                case "REQ.ACT-13": {
                  const status = atUtil.getMonitoringStatus(monitoringStatusData[PARAM.VAL], this.requirementId);
                  if (!this.isStopping) {
                    // AT文言表示
                    this.message = atUtil.getAtMsg(
                      this,
                      this.systemId,
                      this.requirementId,
                      parseInt(status, 16)
                    );
                    const result = await this.doTerminate();
                    if (result) {
                      // 初期表示処理(ユーザ操作で停止していない場合のみ)
                      this.displayInit();
                    } else {
                      this.flow = FLOW.ERR;
                      this.doErrorProcess(ERR_KEY.COMMON);
                    }
                  }
                  break;
                }
                default:
                  break;
              }
            } else {
              // 異常終了
              // completed時のエラー処理はコールバックで行う
            }
            break;

          case REQUEST_STATUS_FAILED: {
            // エラー
            this.flow = FLOW.ERR;
            const errKey = atUtil.getErrKeyByAsyncCd(response.data.async_code);
            this.doErrorProcess(errKey);
            break;
          }
          default:
            atUtil.showUnexpectedError(this);
            break;
        }
      }
      return;
    },

    /** 駆動停止 */
    async doStop() {
      this.isStopping = true;

      // REQ.ACT-27の場合、AT文言：「停止中」
      if (this.requirementId === "REQ.ACT-27") {
        this.message = this.$t("individual.label_active_test_message_hev_stopping");
      }

      // 実行のみだが、停止ボタンを押せるもの
      switch (this.requirementId) {
        case "REQ.ACT.31.NIN.NEXM.NSTP.NSTM.NRD.NLP": {
          // AT文言を表示
          this.message = atUtil.getAtMsg(
            this,
            this.systemId,
            this.requirementId,
            null,
            this.isDriveFlow,
          );
          const result = await this.doTerminate();
          if (result) {
            // 初期表示処理
            this.displayInit();
            // アクティブ文言のクリア判定
            if (atUtil.isClearMessageOnStop(this.requirementId)) {
              this.clearAtMsg();
            }
          } else {
            this.flow = FLOW.ERR;
            this.doErrorProcess(ERR_KEY.COMMON);
          }
          return null;
        }
        default:
          break;
      }

      // アクティブテスト実行API(GET)のレスポンスチェックがin_progress時のコールバック
      const inProgressCallback = function () {
        return config.INDIVIDUAL_POLLING_RESULT_CONTINUE;
      };

      // アクティブテスト実行API(GET)レスポンスチェックがcompleted時のコールバック
      const completedCallback = function (getApiResponse, self) {
        let result = "";
        if (getApiResponse.data[AT_EXE.REQUEST_RESULT] === REQUEST_RESULT.IGNORE) {
          // 停止要求が無効だった場合、何もせずに処理を終了する
          result = config.INDIVIDUAL_POLLING_RESULT_FINISH;
          return result;
        }

        const monitoringStatusData = atUtil.getMonitoringStatusData(getApiResponse.data[PARAM.USER_DATA]);
        result = config.INDIVIDUAL_POLLING_RESULT_ERROR;
        const isStopOnly =
          getApiResponse.data[PARAM.USER_DATA].length === 0 &&
          getApiResponse.data[PARAM.MNTR_STATUS] === MNTR_STATUS.COMPLETE;
        if (
          isStopOnly ||
          !(PARAM.MNTR_STATUS in getApiResponse.data) ||
          !getApiResponse.data[PARAM.MNTR_STATUS]
        ) {
          // 停止
          if (isStopOnly || monitoringStatusData[PARAM.RES_TYPE] === RES_TYPE.OK) {
            // 正常終了
            switch (self.requirementId) {
              // --- 停止監視に遷移（CONTINUE） ---
              case "REQ.ACT.2F.NIN.EXM.STP.STM.RD.NLP":
              case "REQ.ACT.31.NIN.EXM.STP.STM.NRD.NLP":
              case "REQ.ACT.SP18":
              case "REQ.ACT.SP19":
              case "REQ.ACT.SP21":
              case "REQ.ACT.SP23":
              case "REQ.ACT.SP25":
              case "REQ.ACT.SP29":
              case "REQ.ACT-12":
              case "REQ.ACT-27":
                self.flow = FLOW.CHECK_STOP;
                result = config.INDIVIDUAL_POLLING_RESULT_CONTINUE;
                break;
              // --- AT文言を表示してFINISH ---
              case "REQ.ACT.2F.NIN.EXM.STP.NSTM.NRD.LP":
              case "REQ.ACT.31.NIN.EXM.STP.NSTM.NRD.NLP":
              case "REQ.ACT.SP6":
              case "REQ.ACT.SP2": {
                self.message = atUtil.getAtMsg(
                  self,
                  self.systemId,
                  self.requirementId,
                  (self.requirementId === "REQ.ACT.SP2") ? "0" : null, // SP2のみAT文言の未実行表示
                  self.isDriveFlow,
                );
                result = config.INDIVIDUAL_POLLING_RESULT_FINISH;
                break;
              }
              // --- FINISHのみ ---
              case "REQ.ACT.SP16":
              case "REQ.ACT.SP26":
              case "REQ.ACT.SP28":
                result = config.INDIVIDUAL_POLLING_RESULT_FINISH;
                break;
              default:
                break;
            }
          } else {
            // 異常終了
            switch (self.requirementId) {
              case "REQ.ACT.2F.NIN.EXM.STP.STM.RD.NLP":
              case "REQ.ACT.2F.NIN.EXM.STP.NSTM.NRD.LP":
              case "REQ.ACT.31.NIN.EXM.STP.STM.NRD.NLP":
              case "REQ.ACT.31.NIN.EXM.STP.NSTM.NRD.NLP":
              case "REQ.ACT.SP6":
              case "REQ.ACT.SP16":
              case "REQ.ACT.SP18":
              case "REQ.ACT.SP19":
              case "REQ.ACT.SP21":
              case "REQ.ACT.SP23":
              case "REQ.ACT.SP25":
              case "REQ.ACT.SP26":
              case "REQ.ACT.SP28":
                // 共通エラーへ遷移
                self.flow = FLOW.ERR;
                self.doErrorProcess(ERR_KEY.COMMON);
                result = config.INDIVIDUAL_POLLING_RESULT_FINISH;
                break;
              case "REQ.ACT.SP29":
              case "REQ.ACT-12":
              case "REQ.ACT-27": {
                const nrc = atUtil.getNRCStatus(monitoringStatusData[AT_EXE.VAL]);
                // NRC0x22の場合は停止NRCエラー、その他は共通エラー
                const errKey = [NRC_CODE.X22].includes(nrc)
                  ? ERR_KEY.STOP_NRC : ERR_KEY.COMMON;
                self.doErrorProcess(errKey);
                result = config.INDIVIDUAL_POLLING_RESULT_FINISH;
                break;
              }
              default:
                break;
            }
          }
        } else {
          // 停止監視
          if (monitoringStatusData[PARAM.RES_TYPE] === RES_TYPE.OK) {
            // 正常終了
            const status = atUtil.getMonitoringStatus(monitoringStatusData[PARAM.VAL], self.requirementId);

            switch (self.requirementId) {
              case "REQ.ACT.2F.NIN.EXM.STP.STM.RD.NLP":
              case "REQ.ACT.31.NIN.EXM.STP.STM.NRD.NLP":
              case "REQ.ACT.SP18":
              case "REQ.ACT.SP19":
              case "REQ.ACT.SP21": {
                let stopMntrContinue =
                  self.selectedAtFunc[PARAM.STOP_MNTR_CONTINUE] &&
                  self.selectedAtFunc[PARAM.STOP_MNTR_CONTINUE].includes(status);
                const stopMntrTerminate =
                  self.selectedAtFunc[PARAM.STOP_MNTR_TERMINATE] &&
                  self.selectedAtFunc[PARAM.STOP_MNTR_TERMINATE].includes(status);

                // トレーニングモード時は停止監視継続をしない
                if (self.isTestMode) {
                  stopMntrContinue = false;
                }

                // AT文言表示
                self.message = atUtil.getAtMsg(
                  self,
                  self.systemId,
                  self.requirementId,
                  parseInt(status, 16),
                  !self.isStopping,
                  () => { },
                  stopMntrContinue // 停止監視継続条件で文言分岐
                );

                // トレーニングモードの場合は無条件で停止監視終了条件
                if (self.isTestMode) {
                  result = config.INDIVIDUAL_POLLING_RESULT_FINISH;
                  break;
                }

                if (stopMntrContinue) {
                  // 停止監視継続条件
                  result = config.INDIVIDUAL_POLLING_RESULT_CONTINUE;
                } else if (stopMntrTerminate) {
                  // 停止監視終了条件
                  // ポーリング終了後に初期表示処理
                  result = config.INDIVIDUAL_POLLING_RESULT_FINISH;
                } else {
                  // それ以外
                  // ポーリング終了後に初期表示処理
                  result = config.INDIVIDUAL_POLLING_RESULT_FINISH;
                }
                break;
              }
              case "REQ.ACT.SP23":
              case "REQ.ACT.SP25": {
                const stopMntrContinue =
                  self.selectedAtFunc[PARAM.STOP_MNTR_CONTINUE] &&
                  self.selectedAtFunc[PARAM.STOP_MNTR_CONTINUE].includes(status);
                const stopMntrTerminate =
                  self.selectedAtFunc[PARAM.STOP_MNTR_TERMINATE] &&
                  self.selectedAtFunc[PARAM.STOP_MNTR_TERMINATE].includes(status);
                // AT文言表示
                self.message = atUtil.getAtMsg(
                  self,
                  self.systemId,
                  self.requirementId,
                  parseInt(status, 16),
                  self.isDriveFlow,
                  null,
                  !stopMntrContinue
                );
                if (stopMntrContinue) {
                  // 停止監視継続条件
                  result = config.INDIVIDUAL_POLLING_RESULT_CONTINUE;
                } else if (stopMntrTerminate) {
                  // 停止監視終了条件
                  // ポーリング終了後に初期表示処理
                  result = config.INDIVIDUAL_POLLING_RESULT_FINISH;
                } else {
                  // それ以外
                  // ポーリング終了後に初期表示処理
                  result = config.INDIVIDUAL_POLLING_RESULT_FINISH;
                }
                break;
              }
              case "REQ.ACT-12": {
                const stopMntrContinue =
                  self.selectedAtFunc[PARAM.STOP_MNTR_CONTINUE] &&
                  self.selectedAtFunc[PARAM.STOP_MNTR_CONTINUE].includes(status);
                const stopMntrTerminate =
                  self.selectedAtFunc[PARAM.STOP_MNTR_TERMINATE] &&
                  self.selectedAtFunc[PARAM.STOP_MNTR_TERMINATE].includes(status);
                if (stopMntrContinue) {
                  // 停止監視継続条件
                  self.message = atUtil.getAtMsg(
                    self,
                    self.systemId,
                    self.requirementId
                  );
                  result = config.INDIVIDUAL_POLLING_RESULT_CONTINUE;
                } else if (
                  stopMntrTerminate ||
                  (!self.selectedAtFunc[PARAM.STOP_MNTR_CONTINUE] && !self.selectedAtFunc[PARAM.STOP_MNTR_TERMINATE])
                ) {
                  // 停止監視終了条件もしくは停止監視_終了条件,停止監視_継続条件を持たない場合
                  // ポーリング終了後に初期表示処理
                  self.message = atUtil.getAtMsg(
                    self,
                    self.systemId,
                    self.requirementId,
                    parseInt(status, 16),
                    self.isDriveFlow
                  );
                  result = config.INDIVIDUAL_POLLING_RESULT_FINISH;
                } else {
                  // それ以外
                  // 通信エラーへ遷移
                  self.flow = FLOW.ERR_COMMUNICATE;
                  self.doErrorProcess(ERR_KEY.COMMUNICATION);
                  result = config.INDIVIDUAL_POLLING_RESULT_FINISH;
                }
                break;
              }
              case "REQ.ACT.SP29":
                if (
                  self.selectedAtFunc[PARAM.STOP_MNTR_TERMINATE] &&
                  self.selectedAtFunc[PARAM.STOP_MNTR_TERMINATE].includes(status)
                ) {
                  // 停止監視終了条件
                  // AT文言表示
                  self.message = atUtil.getAtMsg(
                    self,
                    self.systemId,
                    self.requirementId,
                    null,
                    self.isDriveFlow
                  );
                  // ポーリング終了後に初期表示処理
                  result = config.INDIVIDUAL_POLLING_RESULT_FINISH;
                } else {
                  result = config.INDIVIDUAL_POLLING_RESULT_CONTINUE;
                }
                break;
              case "REQ.ACT-27":
                if (status === "02") {
                  // AT文言に停止を表示
                  self.message = atUtil.getAtMsg(
                    self,
                    self.systemId,
                    self.requirementId,
                    null,
                    false
                  );
                } else {
                  // 上記以外：共通エラー
                  self.doErrorProcess(ERR_KEY.COMMON);
                }
                result = config.INDIVIDUAL_POLLING_RESULT_FINISH;
                break;
              default:
                result = config.INDIVIDUAL_POLLING_RESULT_FINISH;
                break;
            }
          } else {
            // 異常終了
            switch (self.requirementId) {
              case "REQ.ACT.SP29":
              case "REQ.ACT-12": {
                const nrc = atUtil.getNRCStatus(monitoringStatusData[AT_EXE.VAL]);
                // NRC0x22の場合は停止NRCエラー、その他は共通エラー
                const errKey = [NRC_CODE.X22].includes(nrc)
                  ? ERR_KEY.STOP_NRC : ERR_KEY.COMMON;
                self.doErrorProcess(errKey);
                result = config.INDIVIDUAL_POLLING_RESULT_FINISH;
                break;
              }
              case "REQ.ACT-27":
                if ([NRC_CODE.X21].includes(atUtil.getNRCStatus(monitoringStatusData[AT_EXE.VAL]))) {
                  // 否定応答かつNRC0x21:ポーリング継続
                  // AT文言：「停止中」を継続
                  self.message = self.$t("individual.label_active_test_message_hev_stopping");
                  result = config.INDIVIDUAL_POLLING_RESULT_CONTINUE;
                } else {
                  // 上記以外(主にNRC0x24が該当)：「停止」表示、正常終了
                  self.message = atUtil.getAtMsg(
                    self,
                    self.systemId,
                    self.requirementId,
                    null,
                    false
                  );
                  result = config.INDIVIDUAL_POLLING_RESULT_FINISH;
                }
                break;
              default:
                // 共通エラーに遷移
                self.flow = FLOW.ERR;
                self.doErrorProcess(ERR_KEY.COMMON);
                result = config.INDIVIDUAL_POLLING_RESULT_FINISH;
                break;
            }
          }
        }
        return result;
      };

      // アクティブテスト実行API(GET)のレスポンスチェックがfailed時のコールバック
      const failedCallback = function (getApiResponse, self) {
        // 共通エラーへ遷移
        self.flow = FLOW.ERR;
        self.doErrorProcess(ERR_KEY.COMMON);
      };

      // API実行パラメータ作成
      const token = session.getToken();
      const atStatus = this.getAtStatus();
      const requestBody = atUtil.genReqBodyStop(
        token.id_token,
        this.selectedAtFunc,
        undefined,
        undefined,
        atStatus
      );

      // stop API実行
      const response = await atUtil.pollingAtApi(
        this,
        this.netAppId,
        this.systemId,
        this.activeTestId,
        requestBody,
        // エラー発生のコールバック
        (errorType) => {
          errorType;
          // 他のエラーが発生していない場合に限り、例外エラーダイアログを表示する
          if (!FLOW_ERROR.includes(this.flow)) {
            atUtil.showUnexpectedError(this);
          }
        },
        // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
        (result) => {
          atUtil.validateErrorResult(this, result);
        },
        // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
        (result) => {
          atUtil.validateErrorResult(this, result);
        },
        // GET API レスポンスチェックがin_progress時のコールバック
        inProgressCallback,
        // GET API レスポンスチェックがcompleted時のコールバック
        completedCallback,
        // GET API レスポンスチェックがfailed時のコールバック
        failedCallback
      );

      if (response) {
        if (response.data[AT_EXE.REQUEST_RESULT] === REQUEST_RESULT.IGNORE) {
          // 停止要求が無効だった場合、何もせずに処理を終了する
          return response;
        }

        const monitoringStatusData = atUtil.getMonitoringStatusData(response.data[PARAM.USER_DATA]);
        switch (response.data.status) {
          case REQUEST_STATUS_COMPLETED: {
            // completed
            const isStopOnly = monitoringStatusData.length === 0 && response.data[PARAM.MNTR_STATUS] === MNTR_STATUS.COMPLETE;
            if (
              isStopOnly ||
              (response.data[PARAM.USER_DATA].length > 0 && monitoringStatusData[PARAM.RES_TYPE] === RES_TYPE.OK)
            ) {
              // 正常終了
              switch (this.requirementId) {
                case "REQ.ACT.2F.NIN.EXM.STP.STM.RD.NLP":
                case "REQ.ACT.31.NIN.EXM.STP.STM.NRD.NLP":
                case "REQ.ACT.31.NIN.EXM.STP.NSTM.NRD.NLP":
                case "REQ.ACT.2F.NIN.EXM.STP.NSTM.NRD.LP":
                case "REQ.ACT.SP6":
                case "REQ.ACT.SP16":
                case "REQ.ACT.SP18":
                case "REQ.ACT.SP19":
                case "REQ.ACT.SP21":
                case "REQ.ACT.SP23":
                case "REQ.ACT.SP25":
                case "REQ.ACT.SP26":
                case "REQ.ACT.SP28":
                case "REQ.ACT.SP29":
                  // 初期表示処理はdoTerminate後に実施する
                  break;
                default:
                  break;
              }
            } else {
              // 異常終了
              // completed時のエラー処理はコールバックで行う
            }
            break;
          }
          case REQUEST_STATUS_FAILED: {
            const errKey = Object.keys(ERR_KEY).find(
              (key) => ERR_KEY[key] === response.data.async_code
            );
            atUtil.showUnexpectedError(
              this,
              errKey,
              this.requirementId,
              response.data.async_message
            );
            break;
          }
          default:
            atUtil.showUnexpectedError(this);
            break;
        }
      }
      return response;
    },

    /** 正常時のAT終了処理 
     * @async
     * @return {boolean} terminate result - false is faild.
     */
    async doTerminate() {
      const response = await atUtil.doTerminate(this, false);
      if (response) {
        if (response.data.status === REQUEST_STATUS_COMPLETED) {
          // 正常終了
          if (!response.data.user_data
            || response.data.user_data.length === 0
            || response.data.user_data[0][PARAM.RES_TYPE] === RES_TYPE.OK) {
            return true;
          }
        }
      }

      return false;
    },

    // ================ エラー画面表示処理 ================
    /** 通信エラー画面処理 */
    async communicateError() {
      this.isStopping = true;

      // API実行パラメータ作成
      const token = session.getToken();
      const requestBody = atUtil.genReqBodyStop(
        token.id_token,
        this.selectedAtFunc,
        0,
        ERR_TYPE.COMMUNICATE
      );

      const response = await atUtil.pollingAtApi(
        this,
        this.netAppId,
        this.systemId,
        this.activeTestId,
        requestBody,
        // エラー発生のコールバック
        (errorType) => {
          errorType;
          atUtil.showUnexpectedError(this);
        },
        // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
        (result) => {
          atUtil.validateErrorResult(this, result);
        },
        // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
        (result) => {
          atUtil.validateErrorResult(this, result);
        },
        // GET API レスポンスチェックがin_progress時のコールバック
        () => { },
        // GET API レスポンスチェックがcompleted時のコールバック
        (getApiResponse, self) => {
          const monitoringStatusData = atUtil.getMonitoringStatusData(getApiResponse.data[PARAM.USER_DATA]);
          let result = config.INDIVIDUAL_POLLING_RESULT_ERROR;
          const isStopOnly =
            getApiResponse.data[PARAM.USER_DATA].length === 0 &&
            getApiResponse.data[PARAM.MNTR_STATUS] === MNTR_STATUS.COMPLETE;
          if (
            isStopOnly ||
            !(PARAM.MNTR_STATUS in getApiResponse.data) ||
            !getApiResponse.data[PARAM.MNTR_STATUS]
          ) {
            // 停止
            if (isStopOnly || monitoringStatusData[PARAM.RES_TYPE] === RES_TYPE.OK) {
              // 正常終了
              switch (self.requirementId) {
                case "REQ.ACT.2F.NIN.EXM.STP.NSTM.NRD.LP":
                case "REQ.ACT.31.NIN.EXM.STP.NSTM.NRD.NLP":
                case "REQ.ACT.SP2":
                case "REQ.ACT.SP6":
                case "REQ.ACT.SP26":
                case "REQ.ACT.SP28":
                case "REQ.ACT.SP29": {
                  // 通信エラーを表示
                  atUtil.showUnexpectedError(
                    self,
                    ERR_KEY.COMMUNICATION,
                    self.requirementId,
                    null,
                    async () => {
                      // Terminate中に他の操作をさせないようにする
                      self.displayProcessing = true;
                      await atUtil.doTerminate(self, true);
                      self.displayInit();
                      self.$emit('at-failure');
                      self.displayProcessing = false;
                    }
                  );
                  result = config.INDIVIDUAL_POLLING_RESULT_FINISH;
                  break;
                }
                default:
                  // 停止監視に遷移
                  result = config.INDIVIDUAL_POLLING_RESULT_CONTINUE;
                  break;
              }
            } else {
              // 異常終了
              // 共通エラーへ遷移
              self.flow = FLOW.ERR;
              self.doErrorProcess(ERR_KEY.COMMON);
              result = config.INDIVIDUAL_POLLING_RESULT_FINISH;
            }
          } else {
            // 停止監視
            if (monitoringStatusData[PARAM.RES_TYPE] === RES_TYPE.OK) {
              // 正常終了
              switch (self.requirementId) {
                case "REQ.ACT.2F.NIN.EXM.STP.STM.RD.NLP":
                case "REQ.ACT.31.NIN.EXM.STP.STM.NRD.NLP":
                case "REQ.ACT.SP21":
                case "REQ.ACT.SP19":
                case "REQ.ACT.SP23":
                case "REQ.ACT.SP25":
                case "REQ.ACT-12":
                case "REQ.ACT-27": {
                  const status = atUtil.getMonitoringStatus(monitoringStatusData[PARAM.VAL], self.requirementId);
                  const stopMntrContinue =
                    self.selectedAtFunc[PARAM.STOP_MNTR_CONTINUE] &&
                    self.selectedAtFunc[PARAM.STOP_MNTR_CONTINUE].includes(status);
                  if (stopMntrContinue) {
                    // 停止監視継続条件
                    result = config.INDIVIDUAL_POLLING_RESULT_CONTINUE;
                  } else {
                    // 通信エラーを表示
                    atUtil.showUnexpectedError(
                      self,
                      ERR_KEY.COMMUNICATION,
                      self.requirementId,
                      null,
                      async () => {
                        // Terminate中に他の操作をさせないようにする
                        self.displayProcessing = true;
                        await atUtil.doTerminate(self, true);
                        self.displayInit();
                        self.$emit('at-failure');
                        self.displayProcessing = false;
                      }
                    );
                    result = config.INDIVIDUAL_POLLING_RESULT_FINISH;
                  }
                  break;
                }
                default:
                  break;
              }
            } else {
              // 異常終了
              // 共通エラーへ遷移
              self.flow = FLOW.ERR;
              self.doErrorProcess(ERR_KEY.COMMON);
              result = config.INDIVIDUAL_POLLING_RESULT_FINISH;
            }
          }
          return result;
        },
        // GET API レスポンスチェックがfailed時のコールバック
        (_, self) => {
          self.flow = FLOW.ERR;
          self.doErrorProcess(ERR_KEY.COMMON);
        }
      );
      return response;
    },

    /** STATUSエラー画面処理 */
    async statusError(atMsg) {
      this.isStopping = true;

      // SP18とREQ-13とREQ.ACT.31.NIN.EXM.NSTP.NSTM.NRD.NLPの場合、停止処理はしない(terminateはする)
      if (
        this.requirementId === "REQ.ACT.SP18" ||
        this.requirementId === "REQ.ACT-13" ||
        this.requirementId === "REQ.ACT.31.NIN.EXM.NSTP.NSTM.NRD.NLP"
      ) {
        // STATUSエラーを表示
        atUtil.showUnexpectedError(
          this,
          ERR_KEY.STATUS,
          this.requirementId,
          atMsg,
          async () => {
            // Terminate中に他の操作をさせないようにする
            this.displayProcessing = true;
            await atUtil.doTerminate(this, true);
            this.displayInit();
            this.$emit('at-failure');
            this.displayProcessing = false;
          }
        );

        return null;
      }

      // API実行パラメータ作成
      const token = session.getToken();
      const requestBody = atUtil.genReqBodyStop(
        token.id_token,
        this.selectedAtFunc,
        0,
        ERR_TYPE.STATUS
      );

      const response = await atUtil.pollingAtApi(
        this,
        this.netAppId,
        this.systemId,
        this.activeTestId,
        requestBody,
        // エラー発生のコールバック
        () => {
          // 異常終了: STATUSエラー画面表示
          this.showStatusError(atMsg);
        },
        // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
        () => {
          // 異常終了: STATUSエラー画面表示
          this.showStatusError(atMsg);
        },
        // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
        () => {
          // 異常終了: STATUSエラー画面表示
          this.showStatusError(atMsg);
        },
        // GET API レスポンスチェックがin_progress時のコールバック
        () => { },
        // GET API レスポンスチェックがcompleted時のコールバック
        (getApiResponse, self) => {
          const monitoringStatusData = atUtil.getMonitoringStatusData(getApiResponse.data[PARAM.USER_DATA]);
          let result = config.INDIVIDUAL_POLLING_RESULT_ERROR;
          const isStopOnly =
            getApiResponse.data[PARAM.USER_DATA].length === 0 &&
            getApiResponse.data[PARAM.MNTR_STATUS] === MNTR_STATUS.COMPLETE;
          if (isStopOnly) {
            // STATUSエラーを表示
            atUtil.showUnexpectedError(
              self,
              ERR_KEY.STATUS,
              self.requirementId,
              atMsg,
              async () => {
                // Terminate中に他の操作をさせないようにする
                self.displayProcessing = true;
                await atUtil.doTerminate(self, true);
                self.displayInit();
                self.$emit('at-failure');
                self.displayProcessing = false;
              }
            );
            result = config.INDIVIDUAL_POLLING_RESULT_FINISH;
          } else if (
            !(PARAM.MNTR_STATUS in getApiResponse.data) ||
            !getApiResponse.data[PARAM.MNTR_STATUS]
          ) {
            // 停止
            if (monitoringStatusData[PARAM.RES_TYPE] === RES_TYPE.OK) {
              // 正常終了
              // 停止監視に遷移
              result = config.INDIVIDUAL_POLLING_RESULT_CONTINUE;
            } else {
              // 異常終了: STATUSエラー画面表示
              self.showStatusError(atMsg);
              result = config.INDIVIDUAL_POLLING_RESULT_FINISH;
            }
          } else {
            // 停止監視
            if (monitoringStatusData[PARAM.RES_TYPE] === RES_TYPE.OK) {
              // 正常終了
              switch (self.requirementId) {
                case "REQ.ACT.2F.NIN.EXM.STP.STM.RD.NLP":
                case "REQ.ACT.31.NIN.EXM.STP.STM.NRD.NLP":
                case "REQ.ACT.SP19":
                case "REQ.ACT.SP21":
                case "REQ.ACT.SP25": {
                  const status = atUtil.getMonitoringStatus(monitoringStatusData[PARAM.VAL], self.requirementId);
                  const stopMntrContinue =
                    self.selectedAtFunc[PARAM.STOP_MNTR_CONTINUE] &&
                    self.selectedAtFunc[PARAM.STOP_MNTR_CONTINUE].includes(status);
                  if (stopMntrContinue) {
                    // 停止監視継続条件
                    result = config.INDIVIDUAL_POLLING_RESULT_CONTINUE;
                  } else {
                    // STATUSエラーを表示
                    atUtil.showUnexpectedError(
                      self,
                      ERR_KEY.STATUS,
                      self.requirementId,
                      atMsg,
                      async () => {
                        // Terminate中に他の操作をさせないようにする
                        self.displayProcessing = true;
                        await atUtil.doTerminate(self, true);
                        self.displayInit();
                        self.$emit('at-failure');
                        self.displayProcessing = false;
                      }
                    );
                    result = config.INDIVIDUAL_POLLING_RESULT_FINISH;
                  }
                  break;
                }
                default:
                  break;
              }
            } else {
              // 異常終了: STATUSエラー画面表示
              self.showStatusError(atMsg);
              result = config.INDIVIDUAL_POLLING_RESULT_FINISH;
            }
          }
          return result;
        },
        // GET API レスポンスチェックがfailed時のコールバック
        () => {
          // 異常終了: STATUSエラー画面表示
          this.showStatusError(atMsg);
        }
      );
      return response;
    },

    /** NRCエラー画面処理 */
    async nrcError() {
      this.isStopping = true;

      // API実行パラメータ作成
      const token = session.getToken();
      const requestBody = atUtil.genReqBodyStop(
        token.id_token,
        this.selectedAtFunc,
        0,
        ERR_TYPE.NRC
      );

      const response = await atUtil.pollingAtApi(
        this,
        this.netAppId,
        this.systemId,
        this.activeTestId,
        requestBody,
        // エラー発生のコールバック
        (errorType) => {
          errorType;
          atUtil.showUnexpectedError(this);
        },
        // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
        (result) => {
          atUtil.validateErrorResult(this, result);
        },
        // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
        (result) => {
          atUtil.validateErrorResult(this, result);
        },
        // GET API レスポンスチェックがin_progress時のコールバック
        () => { },
        // GET API レスポンスチェックがcompleted時のコールバック
        (getApiResponse, self) => {
          const monitoringStatusData = atUtil.getMonitoringStatusData(getApiResponse.data[PARAM.USER_DATA]);
          let result = config.INDIVIDUAL_POLLING_RESULT_ERROR;
          const isStopOnly =
            getApiResponse.data[PARAM.USER_DATA].length === 0 &&
            getApiResponse.data[PARAM.MNTR_STATUS] === MNTR_STATUS.COMPLETE;
          if (
            isStopOnly ||
            !(PARAM.MNTR_STATUS in getApiResponse.data) ||
            !getApiResponse.data[PARAM.MNTR_STATUS]
          ) {
            // 停止
            if (isStopOnly || monitoringStatusData[PARAM.RES_TYPE] === RES_TYPE.OK) {
              // 正常終了
              switch (self.requirementId) {
                case "REQ.ACT.SP28":
                case "REQ.ACT.SP29": {
                  // NRCエラーを表示
                  atUtil.showUnexpectedError(
                    self,
                    ERR_KEY.NRC,
                    self.requirementId,
                    null,
                    async () => {
                      // Terminate中に他の操作をさせないようにする
                      self.displayProcessing = true;
                      await atUtil.doTerminate(self, true);
                      self.displayInit();
                      self.$emit('at-failure');
                      self.displayProcessing = false;
                    }
                  );
                  result = config.INDIVIDUAL_POLLING_RESULT_FINISH;
                  break;
                }
                default:
                  // 停止監視に遷移
                  result = config.INDIVIDUAL_POLLING_RESULT_CONTINUE;
                  break;
              }
            } else {
              // 異常終了: NRCエラー画面表示
              self.showNrcError();
              result = config.INDIVIDUAL_POLLING_RESULT_FINISH;
            }
          } else {
            // 停止監視
            if (monitoringStatusData[PARAM.RES_TYPE] === RES_TYPE.OK) {
              // 正常終了
              switch (self.requirementId) {
                case "REQ.ACT.SP21":
                case "REQ.ACT.SP23":
                case "REQ.ACT.SP25":
                case "REQ.ACT-12":
                case "REQ.ACT-27": {
                  const status = atUtil.getMonitoringStatus(monitoringStatusData[PARAM.VAL], self.requirementId);
                  const stopMntrContinue =
                    self.selectedAtFunc[PARAM.STOP_MNTR_CONTINUE] &&
                    self.selectedAtFunc[PARAM.STOP_MNTR_CONTINUE].includes(status);
                  if (stopMntrContinue) {
                    // 停止監視継続条件
                    result = config.INDIVIDUAL_POLLING_RESULT_CONTINUE;
                  } else {
                    // NRCエラーを表示
                    atUtil.showUnexpectedError(
                      self,
                      ERR_KEY.NRC,
                      self.requirementId,
                      null,
                      async () => {
                        // Terminate中に他の操作をさせないようにする
                        self.displayProcessing = true;
                        await atUtil.doTerminate(self, true);
                        self.displayInit();
                        self.$emit('at-failure');
                        self.displayProcessing = false;
                      }
                    );
                    result = config.INDIVIDUAL_POLLING_RESULT_FINISH;
                  }
                  break;
                }
                default:
                  break;
              }
            } else {
              // 異常終了: NRCエラー画面表示
              self.showNrcError();
              result = config.INDIVIDUAL_POLLING_RESULT_FINISH;
            }
          }
          return result;
        },
        // GET API レスポンスチェックがfailed時のコールバック
        () => {
          self.flow = FLOW.ERR;
          self.doErrorProcess(ERR_KEY.COMMON);
        }
      );
      return response;
    },

    /**
     * STATUSエラー画面表示
     * @param {string} atMsg STATUSエラーで表示するAT文言
     */
    showStatusError(atMsg) {
      atUtil.showUnexpectedError(
        this,
        ERR_KEY.STATUS,
        this.requirementId,
        atMsg,
        async () => {
          // Terminate中に他の操作をさせないようにする
          this.displayProcessing = true;
          await atUtil.doTerminate(this, true);
          this.displayInit();
          this.$emit('at-failure');
          this.displayProcessing = false;
        }
      );
    },

    /** NRCエラー画面表示 */
    showNrcError() {
      atUtil.showUnexpectedError(
        this,
        ERR_KEY.NRC,
        this.requirementId,
        null,
        async () => {
          // Terminate中に他の操作をさせないようにする
          this.displayProcessing = true;
          await atUtil.doTerminate(this, true);
          this.displayInit();
          this.$emit('at-failure');
          this.displayProcessing = false;
        }
      );
    },

    /** 停止NRCエラー画面処理 */
    stopNrcError() {
      // 停止NRCエラーを表示
      atUtil.showUnexpectedError(
        this,
        ERR_KEY.STOP_NRC,
        this.requirementId,
        null, // SP29,REQ-12,REQ-27のみなのでnull
        async () => {
          // Terminate中に他の操作をさせないようにする
          this.displayProcessing = true;
          await atUtil.doTerminate(this, true);
          this.displayInit();
          this.$emit('at-failure');
          this.displayProcessing = false;
        }
      );
    },

    /** 共通エラー画面処理 */
    commonError() {
      // 共通エラーを表示
      atUtil.showUnexpectedError(
        this,
        ERR_KEY.COMMON,
        this.requirementId,
        null, // 共通エラーはnull
        async () => {
          // Terminate中に他の操作をさせないようにする
          this.displayProcessing = true;
          await atUtil.doTerminate(this, true);
          this.displayInit();
          this.$emit('at-failure');
          this.displayProcessing = false;
        }
      );
    },

    /** タイムアウトエラー画面処理 */
    timeoutError() {
      // タイムアウトエラーを表示
      atUtil.showUnexpectedError(
        this,
        ERR_KEY.TIMEOUT,
        this.requirementId,
        null, // タイムアウトエラーはnull
        async () => {
          // Terminate中に他の操作をさせないようにする
          this.displayProcessing = true;
          await atUtil.doTerminate(this, true);
          this.displayInit();
          this.$emit('at-failure');
          this.displayProcessing = false;
        }
      );
    },

    /** 
     * エラー処理の振り分け
     * @param {*} errKey エラーキー
     * @param {string} [atMsg=""] STATUSエラーで表示するAT文言
     */
    async doErrorProcess(errKey, atMsg = "") {
      switch (errKey) {
        case ERR_KEY.COMMUNICATION:
          this.flow = FLOW.ERR_COMMUNICATE;
          await this.communicateError();
          break;
        case ERR_KEY.STATUS:
          this.flow = FLOW.ERR_STATUS;
          await this.statusError(atMsg);
          break;
        case ERR_KEY.NRC:
          this.flow = FLOW.ERR_NRC;
          await this.nrcError();
          break;
        case ERR_KEY.STOP_NRC:
          this.flow = FLOW.ERR_NRC;
          this.stopNrcError();
          break;
        case ERR_KEY.TIMEOUT:
          this.flow = FLOW.ERR_TIMEOUT;
          this.timeoutError();
          break;
        case ERR_KEY.COMMON:
        default:
          this.flow = FLOW.ERR;
          this.commonError();
          break;
      }
    },

    /** キャンセル処理 */
    async cancelExec() {
      // 駆動中であれば停止する。ただし他のエラー処理を既に実行していれば何もしない
      if (this.isDriveFlow && !FLOW_ERROR.includes(this.flow) && !NO_ERROR_STOP_REQ.includes(this.requirementId)) {
        this.flow = FLOW.STOP;
        await this.doStop();
      }
    },

    setSignout() {
      this.signout = true;
    },

  },

  mounted() {
    // noop
  },
};
